import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import {FmlxSwitch, FmlxIcon, FmlxButton, FmlxTooltip} from 'fmlx-common-ui';
import _ from 'lodash';

import SerialDilutionImage from './SerialDilutionImage';

import SerialDilutionDeckSelectionButton from './SerialDilutionDeckSelectionButton';

import {
  setSerialDilutionEditStep,
  setSerialDilutionStep,
  setSerialDilutionPrevStep,
} from '../../../stores/actions/protocolEditorAction';
import {globalAddUndoItem} from '../../../stores/business/undoRedoBusinessAction';
import {
  getSdRawTaskIds,
  getSdWellGroupName,
  removeSdRawTasks,
  saveSdTemporaryStates,
  isSdStepDefined,
  removeSdDiluentTargetRawTask,
  handleStepPropertiesChange,
  handleSDPropertyChange,
  applyUniformedVolumeToNewSteps,
} from '../../../stores/business/taskListSerialDilutionBusiness';
import {setSdPropertyObject, removeSdPropertyObject} from '../../../stores/actions/cardSerialDilutionStateAction';
import {SDVolumePropertyKeyEnum, SerialDilutionStep, TrayType} from '../../../constants/dataEnum';
import * as TemporaryStateType from '../../../constants/temporaryStateType';
import SerialDilutionSerialPlateTubes from './SerialDilutionSerialPlateTubes';

import withIntl from '../../../lib/withIntl';

import RatioTextBox from '../../../components/RatioTextBox';

import './SerialDilution.scss';
import ArrowVolumeBox from './ArrowVolumeBox/ArrowVolumeBox';
import {getCards} from '../../../stores/selectors/cardStateSelector';
import ReplicateWellOption from './ReplicateWellOption';

function SerialDilution({
  protocolEditorStateSerialDilutionEditStep,
  labwareState,
  cardID,
  protocolEditorStateActionSetSerialDilutionStep,
  protocolEditorStateActionSetSerialDilutionEditStep,
  protocolEditorStateActionSetSerialDilutionPrevStep,
  undoRedoBusinessActionGlobalAddUndoItem,
  taskListSerialDilutionBusinessSaveSdTemporaryStates,
  taskListSerialDilutionBusinessGetSdRawTaskIds,
  taskListSerialDilutionBusinessRemoveSdRawTasks,
  taskListSerialDilutionBusinessRemoveSdDiluentTargetRawTask,
  taskListSerialDilutionBusinessIsSdStepDefined,
  taskListSerialDilutionBusinessHandleStepPropertiesChange,
  taskListSerialDilutionBusinessHandleSDPropertyChange,
  taskListSerialDilutionBusinessApplyUniformedVolumeToNewSteps,
  serialDilutionActionBusinessSetSdPropertyObject,
  serialDilutionActionBusinessRemoveSdPropertyObject,
  intl,
  minVolumeCapacity,
  recommendedVolumeCapacity,
  isHighVolumeProtocol,
}) {
  const cards = useSelector(getCards);
  const selectedCard = cards[cardID];

  // #region tube
  const [currentlyShownSerialPlateTubesProps, setCurrentlyShownSerialPlateTubesProps] = useState([]);
  const [allSerialPlateTubesProps, setAllSerialPlateTubesProps] = useState([]);
  const [firstSerialPlateTubeIndex, setFirstSerialPlateTubeIndex] = useState(0);
  // #endregion tube

  // #region refs
  const diluentSourceRef = useRef(null);
  const sampleSourceRef = useRef(null);
  const aliquotRef = useRef(null);
  const serialPlateRatioRef = useRef([]);
  const serialPlateRefs = useRef([]);

  const totalStepCountRef = useRef(0);
  // #endregion refs

  // #region constant
  const sampleColor = '#0059cc';
  const diluentColor = '#CCE1FD';
  // eslint-disable-next-line no-unsafe-optional-chaining
  const tubeLiquidOpacityInterval = (1 - 0.3) / selectedCard?.sdProperty?.step;
  const maxPercentage = 80;
  // #endregion constant

  const showSerialDilutionTubes = (startingIndex, dilutionPlateTubes) => {
    const allDilutionTubesElement = dilutionPlateTubes || allSerialPlateTubesProps;
    if (!allDilutionTubesElement || allDilutionTubesElement?.length === 0) return;

    setCurrentlyShownSerialPlateTubesProps(allDilutionTubesElement.slice(startingIndex, startingIndex + 4));
  };

  const getMaxVolume = () => {
    const stepProperties = selectedCard?.sdProperty?.stepProperties ?? [];
    const step = selectedCard?.sdProperty?.step ?? 0;
    const aliquotVolume = selectedCard?.sdProperty?.aliquotVolume ?? 0;

    const sampleVolumeMap = stepProperties.map((prop, idx) => {
      if (idx < step) {
        const stepProp = stepProperties[idx];
        const stepSampleVolume = stepProp?.stepSampleVolume ?? 0;
        const stepDiluentVolume = prop?.stepDiluentVolume ?? 0;
        return stepSampleVolume + stepDiluentVolume;
      }
      return 0;
    });

    const maxVolume = Math.max(...sampleVolumeMap, aliquotVolume);

    return maxVolume;
  };

  const getTubeTooltip = () => {
    if (!selectedCard) {
      return '';
    }
    const sdRawTasks = selectedCard?.sdRawTasks ?? {};

    const [, , , taskDiluentTargetIds] = taskListSerialDilutionBusinessGetSdRawTaskIds(selectedCard.ID);

    const tasks = [];

    if (taskDiluentTargetIds?.length) {
      taskDiluentTargetIds.forEach((id) => {
        tasks.push(sdRawTasks[id]);
      });
    }

    const wellsLabwareMap = {};

    for (let i = 0, ii = tasks.length; i < ii; i += 1) {
      const task = tasks[i];
      if (!task) {
        continue;
      }
      const step = task?.step;
      const taskLabware = Object.values(labwareState).find((e) => e?.LabwareID === task?.labwareID);
      const labwareName = taskLabware?.name;

      if (!(step in wellsLabwareMap)) {
        wellsLabwareMap[step] = {step, wells: [], labwareName};
      }

      const wellGroupName = getSdWellGroupName(
        task.row,
        task.column,
        task.rowTipCount,
        task.columnTipCount,
        taskLabware?.rowCount,
        taskLabware?.colCount,
      );

      wellsLabwareMap[step].wells.push(wellGroupName);
    }

    const wellMap = Object.values(wellsLabwareMap).map((e) => ({
      tooltip: `${e.labwareName} [${e.wells.join('; ')}]`,
      step: e.step,
    }));
    return wellMap || [];
  };

  const generateSerialPlateTubes = (startingIndex, newStep) => {
    const firstIndex = 0;
    const step = newStep ?? selectedCard?.sdProperty?.step ?? 0;
    const totalTubes = step < 4 ? 4 : step;

    const tubesImage = [];

    const tubeTooltips = getTubeTooltip();

    let tubeLiquidOpacity = firstIndex ? currentlyShownSerialPlateTubesProps[firstIndex]?.props?.liquidOpacity : 1;

    const startingPlateIndex = startingIndex || 0;
    const gradationView = selectedCard?.sdProperty?.gradationView;
    const diluentVolume = selectedCard?.sdProperty?.diluentVolume ?? 0;
    const aliquotVolume = selectedCard?.sdProperty?.aliquotVolume ?? 0;
    const stepProperties = selectedCard?.sdProperty?.stepProperties ?? [];

    let maxVolume = aliquotVolume + diluentVolume;

    if (stepProperties.length) {
      maxVolume = getMaxVolume();
    }

    for (let i = 0; i < totalTubes; i += 1) {
      const used = i < step;
      const currentTubeIndex = startingPlateIndex + i;
      const tubeOpacity = used ? 1 : 0.5;

      const currentStepProperty = stepProperties.find((prop) => prop?.step === i + 1);

      const stepSampleVolume = currentStepProperty?.stepSampleVolume;
      const stepTotalVolume = currentStepProperty && stepSampleVolume + (currentStepProperty?.stepDiluentVolume ?? 0);
      const stepTotalPercentage = currentStepProperty && (stepTotalVolume / maxVolume) * maxPercentage;
      const stepSamplePercentage = currentStepProperty && (stepSampleVolume / stepTotalVolume) * 100;

      if (gradationView && used && stepTotalPercentage) {
        tubeLiquidOpacity -= tubeLiquidOpacityInterval;
      } else if (!gradationView && used) {
        tubeLiquidOpacity = 1;
      } else {
        tubeLiquidOpacity = 0;
      }

      const tubeTooltip = tubeTooltips.length && tubeTooltips?.find((e) => e.step === i + 1);

      const tubeElement = {
        percentage: stepTotalPercentage,
        liquidOpacity: (_.isFinite(stepTotalPercentage) && tubeLiquidOpacity) || 0,
        tubeOpacity,
        deletable: used,
        key: `dilution-tube-${currentTubeIndex}`,
        color: sampleColor,
        diluentColor,
        gradation: Boolean(gradationView),
        samplePercentage: stepSamplePercentage,
        tooltip: tubeTooltip?.tooltip,
      };

      tubesImage.push(tubeElement);
    }

    setAllSerialPlateTubesProps(tubesImage);
    setFirstSerialPlateTubeIndex(startingPlateIndex);
    showSerialDilutionTubes(startingIndex, tubesImage);
  };

  const isSdEditing = (step) => {
    const sdRawTasksSummary = selectedCard?.sdRawTasksSummary;

    if (!sdRawTasksSummary) {
      return true;
    }

    const {taskSampleSource, taskDiluentSource, taskSampleTarget, taskDiluentTargets} = sdRawTasksSummary;

    switch (step) {
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        return Boolean(taskDiluentSource);
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        return Boolean(taskSampleSource);
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        return taskSampleTarget?.length;
      case SerialDilutionStep.SET_DILUENT_TARGET:
        return taskDiluentTargets?.length;
      default:
        return false;
    }
  };

  const handleClickButton = async (step) => {
    const isEdit = isSdEditing(step) || protocolEditorStateSerialDilutionEditStep;

    if (isEdit) {
      if (!protocolEditorStateSerialDilutionEditStep) protocolEditorStateActionSetSerialDilutionEditStep(step);
      await taskListSerialDilutionBusinessSaveSdTemporaryStates(
        selectedCard.ID,
        TemporaryStateType.EDIT_FROM_SD_SETTINGS,
      );
    }

    await protocolEditorStateActionSetSerialDilutionPrevStep(SerialDilutionStep.SERIAL_DILUTION_SETTINGS);
    await protocolEditorStateActionSetSerialDilutionStep(step);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const handleDeleteSdStep = useCallback(
    async (sdStep, step, sdRawTasks, startIndex, tubeIndex) => {
      if (sdStep === SerialDilutionStep.SET_DILUENT_TARGET) {
        let startingIndex = startIndex;
        const stepToDelete = tubeIndex + 1 + startingIndex;
        const lastFirstTubeStep = totalStepCountRef.current - 4;

        const idsToRemove = Object.values(sdRawTasks)
          .filter((task) => task.step === stepToDelete)
          .map((task) => task.ID);

        if (
          stepToDelete > lastFirstTubeStep &&
          lastFirstTubeStep !== 0 &&
          firstSerialPlateTubeIndex !== 0 &&
          totalStepCountRef.current > 4
        )
          handleClickBack();

        if (idsToRemove.length) {
          await taskListSerialDilutionBusinessRemoveSdDiluentTargetRawTask(selectedCard.ID, idsToRemove);
          if (startingIndex && startingIndex + 4 === step) {
            startingIndex -= 1;
          }
          undoRedoBusinessActionGlobalAddUndoItem();
        }
        return;
      }

      await taskListSerialDilutionBusinessRemoveSdRawTasks(selectedCard.ID, [sdStep]);

      if (sdStep === SerialDilutionStep.SET_SAMPLE_TARGET) {
        serialDilutionActionBusinessRemoveSdPropertyObject(cardID, SDVolumePropertyKeyEnum.ALIQUOT_VOLUME);
      }

      undoRedoBusinessActionGlobalAddUndoItem();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstSerialPlateTubeIndex],
  );

  const getWellLabel = (step) => {
    if (!selectedCard) {
      return '';
    }
    const sdRawTasks = selectedCard?.sdRawTasks ?? {};

    const [taskDiluentSourceId, taskSampleSourceId, taskSampleTargetIds, taskDiluentTargetIds] =
      taskListSerialDilutionBusinessGetSdRawTaskIds(selectedCard.ID);

    const tasks = [];

    switch (step) {
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        if (taskDiluentSourceId && !sdRawTasks[taskDiluentSourceId]?.skipped) {
          tasks.push(sdRawTasks[taskDiluentSourceId]);
        } else {
          return [{default: 'SET SOURCE'}];
        }
        break;
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        if (taskSampleSourceId && !sdRawTasks[taskSampleSourceId]?.skipped) {
          tasks.push(sdRawTasks[taskSampleSourceId]);
        } else {
          return [{default: 'SET SOURCE'}];
        }
        break;
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        if (taskSampleTargetIds?.length) {
          taskSampleTargetIds.forEach((id) => {
            tasks.push(sdRawTasks[id]);
          });
        } else {
          return [{default: 'SET LOCATION'}];
        }
        break;
      case SerialDilutionStep.SET_DILUENT_TARGET:
        if (taskDiluentTargetIds?.length) {
          taskDiluentTargetIds.forEach((id) => {
            tasks.push(sdRawTasks[id]);
          });
        } else {
          return [{default: 'SET DESTINATION'}];
        }
        break;
      default:
        break;
    }

    const wellsLabwareMap = {};

    for (let i = 0, ii = tasks.length; i < ii; i += 1) {
      const task = tasks[i];
      const taskLabware = Object.values(labwareState).find((e) => e?.LabwareID === task?.labwareID);
      const labwareName = taskLabware?.name;

      if (!(labwareName in wellsLabwareMap)) {
        wellsLabwareMap[labwareName] = {labwareName, wells: []};
      }

      const wellGroupName = getSdWellGroupName(
        task.row,
        task.column,
        task.rowTipCount,
        task.columnTipCount,
        taskLabware?.rowCount,
        taskLabware?.colCount,
      );

      wellsLabwareMap[labwareName].wells.push(wellGroupName);
    }

    const wellMap = Object.values(wellsLabwareMap).map((e) => ({
      labwareName: e.labwareName,
      wells: `[${e.wells.join('; ')}]`,
      step,
    }));
    return wellMap;
  };

  const handleReuseTipsChange = (val) => {
    serialDilutionActionBusinessSetSdPropertyObject(cardID, {reuseTips: val});
  };

  const handleGradationViewChange = (val) => {
    serialDilutionActionBusinessSetSdPropertyObject(cardID, {gradationView: val});
  };

  const handleClickNext = () => {
    showSerialDilutionTubes(firstSerialPlateTubeIndex + 1);
    setFirstSerialPlateTubeIndex(firstSerialPlateTubeIndex + 1);
  };

  const handleClickBack = () => {
    showSerialDilutionTubes(firstSerialPlateTubeIndex - 1);
    setFirstSerialPlateTubeIndex(firstSerialPlateTubeIndex - 1);
  };

  useEffect(() => {
    const {step: newStepCountState, stepProperties} = selectedCard?.sdProperty || {};
    const totalStepCount = totalStepCountRef.current || 0;

    // eslint-disable-next-line no-unsafe-optional-chaining
    if (newStepCountState > totalStepCount && stepProperties?.length - 1 < newStepCountState) {
      taskListSerialDilutionBusinessApplyUniformedVolumeToNewSteps(cardID);
    }

    if (newStepCountState) totalStepCountRef.current = newStepCountState;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);

  useEffect(() => {
    generateSerialPlateTubes(firstSerialPlateTubeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard]);

  const onChekedAllSampleHandler = (e) => {
    serialDilutionActionBusinessSetSdPropertyObject(cardID, {isSetSampleToAllSteps: e.target.checked});

    if (e.target.checked) {
      const newSampleVolume = selectedCard?.sdProperty?.aliquotVolume ?? 0;
      taskListSerialDilutionBusinessHandleSDPropertyChange(
        SDVolumePropertyKeyEnum.ALIQUOT_VOLUME,
        newSampleVolume,
        cardID,
        totalStepCountRef,
      );
    }
  };

  return (
    <div className="serial-dilution">
      <div className="serial-dilution__header">
        <span className="serial-dilution__header__optional-switches">
          <FmlxSwitch size="sm" checked={selectedCard?.sdProperty?.reuseTips} onChange={handleReuseTipsChange} />
          <span style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
            {intl.formatMessage({id: 'protocolEditor.serialDilution.switch.firstSwitch'})}
            <FmlxTooltip title={intl.formatMessage({id: 'protocolEditor.serialDilution.reuseTip.info'})}>
              <span>
                <FmlxIcon name="InfoOutline" size="xs" />
              </span>
            </FmlxTooltip>
          </span>
        </span>
        <span className="serial-dilution__header__optional-switches">
          <FmlxSwitch
            size="sm"
            checked={selectedCard?.sdProperty?.gradationView}
            onChange={handleGradationViewChange}
          />
          {intl.formatMessage({id: 'protocolEditor.serialDilution.switch.secondSwitch'})}
        </span>
      </div>
      <div className="serial-dilution__content">
        <div className="serial-dilution__content__diluent-source">
          <SerialDilutionImage
            imgPath="diluent-source"
            imgRef={diluentSourceRef}
            labelImg={intl.formatMessage({id: 'protocolEditor.serialDilution.deckSelection.diluentSource'})}
            labelWell=""
            label={getWellLabel(SerialDilutionStep.SET_DILUENT_SOURCE)}
            deckInsertNumber="2"
            onClick={() => handleClickButton(SerialDilutionStep.SET_DILUENT_SOURCE)}
            isFilled={taskListSerialDilutionBusinessIsSdStepDefined(cardID, SerialDilutionStep.SET_DILUENT_SOURCE)}
            onDelete={() => handleDeleteSdStep(SerialDilutionStep.SET_DILUENT_SOURCE)}
          />
        </div>
        <div className="serial-dilution__content__sample-source">
          <SerialDilutionImage
            imgStyle={{height: '25vh'}}
            imgRef={sampleSourceRef}
            imgPath="sample-source"
            labelImg={intl.formatMessage({id: 'protocolEditor.serialDilution.deckSelection.sampleSource'})}
            onClick={() => handleClickButton(SerialDilutionStep.SET_SAMPLE_SOURCE)}
            label={getWellLabel(SerialDilutionStep.SET_SAMPLE_SOURCE)}
            isFilled={taskListSerialDilutionBusinessIsSdStepDefined(cardID, SerialDilutionStep.SET_SAMPLE_SOURCE)}
            onDelete={() => handleDeleteSdStep(SerialDilutionStep.SET_SAMPLE_SOURCE)}
          />
        </div>
        <div className="serial-dilution__content__aliquot">
          <SerialDilutionImage
            isRequired
            isTubeComponent
            imgPath="aliquot"
            imgRef={aliquotRef}
            tubeComponenProps={
              taskListSerialDilutionBusinessIsSdStepDefined(cardID, SerialDilutionStep.SET_SAMPLE_TARGET)
                ? {
                    percentage: 80,
                    liquidOpacity:
                      selectedCard?.sdProperty?.sampleVolume ||
                      !taskListSerialDilutionBusinessIsSdStepDefined(cardID, SerialDilutionStep.SET_SAMPLE_SOURCE)
                        ? 1
                        : 0,
                    opacity: 1,
                    color: sampleColor,
                  }
                : {liquidOpacity: 0}
            }
            labelImg={intl.formatMessage({id: 'protocolEditor.serialDilution.deckSelection.aliquot'})}
            onClick={() => handleClickButton(SerialDilutionStep.SET_SAMPLE_TARGET)}
            label={getWellLabel(SerialDilutionStep.SET_SAMPLE_TARGET)}
            isFilled={taskListSerialDilutionBusinessIsSdStepDefined(cardID, SerialDilutionStep.SET_SAMPLE_TARGET)}
            onDelete={() => handleDeleteSdStep(SerialDilutionStep.SET_SAMPLE_TARGET)}
          />
        </div>
        <div className="serial-dilution__content__serial-plates-container relative">
          <span className="serial-dilution__content__serial-plates">
            <FmlxButton
              onlyIcon
              icon={<FmlxIcon name="Previous" />}
              type="basic"
              variant="plain"
              withTooltip={false}
              disabled={firstSerialPlateTubeIndex === 0}
              onClick={handleClickBack}
            />
            <SerialDilutionSerialPlateTubes
              serialPlateRefs={serialPlateRefs}
              tubesProps={currentlyShownSerialPlateTubesProps}
              onDelete={(tubeIndex) =>
                handleDeleteSdStep(
                  SerialDilutionStep.SET_DILUENT_TARGET,
                  selectedCard?.sdProperty?.step ?? 0,
                  selectedCard?.sdRawTasks,
                  firstSerialPlateTubeIndex,
                  tubeIndex,
                )
              }
            />
            <FmlxButton
              onlyIcon
              icon={<FmlxIcon name="Next" />}
              type="basic"
              variant="plain"
              withTooltip={false}
              disabled={
                selectedCard?.sdProperty?.step < 4 ||
                !selectedCard?.sdProperty?.step ||
                firstSerialPlateTubeIndex + 4 === selectedCard?.sdProperty?.step
              }
              onClick={handleClickNext}
            />
          </span>
          <ReplicateWellOption />
          <SerialDilutionDeckSelectionButton
            isRequired
            label={intl.formatMessage({id: 'protocolEditor.serialDilution.deckSelection.serialDilutionPlate'})}
            deckInsertNumber="2"
            labelWell={getWellLabel(SerialDilutionStep.SET_DILUENT_TARGET)[0]?.wells ?? ''}
            onClick={() => handleClickButton(SerialDilutionStep.SET_DILUENT_TARGET)}
            labelButton={getWellLabel(SerialDilutionStep.SET_DILUENT_TARGET)}
          />
          <span className="serial-dilution__content__serial-plates--ratio">
            {Array(4)
              .fill('')
              .map((e, index) => (
                <RatioTextBox
                  innerRef={(el) => {
                    serialPlateRatioRef.current[index] = el;
                  }}
                  value={
                    selectedCard?.sdProperty?.stepProperties
                      ? selectedCard?.sdProperty?.stepProperties[index + firstSerialPlateTubeIndex]?.ratio || [0, 0]
                      : [0, 0]
                  }
                  onBlur={(val) =>
                    taskListSerialDilutionBusinessHandleStepPropertiesChange(
                      'ratio',
                      index + firstSerialPlateTubeIndex + 1,
                      val,
                      cardID,
                    )
                  }
                  placeholder={`1/${10 * (index + 1)}`}
                  style={{width: '20%'}}
                />
              ))}
          </span>
        </div>
      </div>
      <ArrowVolumeBox
        serialPlateRatioRef={serialPlateRatioRef}
        serialPlateRefs={serialPlateRefs}
        diluentSourceRef={diluentSourceRef}
        sampleSourceRef={sampleSourceRef}
        aliquotRef={aliquotRef}
        firstSerialPlateTubeIndex={firstSerialPlateTubeIndex}
        selectedCard={selectedCard}
        onChange={(key, newVal, stepNumber) => {
          if (stepNumber) taskListSerialDilutionBusinessHandleStepPropertiesChange(key, stepNumber, newVal, cardID);
          else taskListSerialDilutionBusinessHandleSDPropertyChange(key, newVal, cardID, totalStepCountRef);
        }}
        onCheckedChange={onChekedAllSampleHandler}
        isSetSampleToAllSteps={selectedCard?.sdProperty?.isSetSampleToAllSteps}
        volumeCapacities={{
          min: minVolumeCapacity,
          recommended: recommendedVolumeCapacity,
        }}
        isHighVolumeProtocol={isHighVolumeProtocol}
      />
    </div>
  );
}

SerialDilution.propTypes = {
  intl: PropTypes.any.isRequired,
  protocolEditorStateSerialDilutionEditStep: PropTypes.number.isRequired,
  labwareState: PropTypes.object.isRequired,
  cardID: PropTypes.number.isRequired,
  protocolEditorStateActionSetSerialDilutionStep: PropTypes.func.isRequired,
  protocolEditorStateActionSetSerialDilutionEditStep: PropTypes.func.isRequired,
  protocolEditorStateActionSetSerialDilutionPrevStep: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessSaveSdTemporaryStates: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessGetSdRawTaskIds: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessRemoveSdRawTasks: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessRemoveSdDiluentTargetRawTask: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessIsSdStepDefined: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessHandleStepPropertiesChange: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessHandleSDPropertyChange: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessApplyUniformedVolumeToNewSteps: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalAddUndoItem: PropTypes.func.isRequired,
  serialDilutionActionBusinessSetSdPropertyObject: PropTypes.func.isRequired,
  serialDilutionActionBusinessRemoveSdPropertyObject: PropTypes.func.isRequired,
  minVolumeCapacity: PropTypes.object.isRequired,
  recommendedVolumeCapacity: PropTypes.object.isRequired,
  isHighVolumeProtocol: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  const {configState, protocolState} = state;
  const {lastOpenedProtocol} = protocolState;
  const {minAspirateVolume, minAspirateHighVolume, recommendedAspirateVolume, recommendedAspirateHighVolume} =
    configState;
  const isHighVolumeProtocol = lastOpenedProtocol?.trayType === TrayType.HIGH_VOLUME_CADDY;

  return {
    protocolEditorStateSerialDilutionEditStep: state.protocolEditorState.serialDilutionEditStep,
    cardStateCards: state.cardState.cards,
    labwareState: state.labwareState,
    minVolumeCapacity: isHighVolumeProtocol ? minAspirateHighVolume : minAspirateVolume,
    recommendedVolumeCapacity: isHighVolumeProtocol ? recommendedAspirateHighVolume : recommendedAspirateVolume,
    isHighVolumeProtocol,
  };
};

const mapDispatchToProps = (dispatch) => ({
  protocolEditorStateActionSetSerialDilutionStep: bindActionCreators(setSerialDilutionStep, dispatch),
  protocolEditorStateActionSetSerialDilutionEditStep: bindActionCreators(setSerialDilutionEditStep, dispatch),
  protocolEditorStateActionSetSerialDilutionPrevStep: bindActionCreators(setSerialDilutionPrevStep, dispatch),
  taskListSerialDilutionBusinessSaveSdTemporaryStates: bindActionCreators(saveSdTemporaryStates, dispatch),
  taskListSerialDilutionBusinessGetSdRawTaskIds: bindActionCreators(getSdRawTaskIds, dispatch),
  taskListSerialDilutionBusinessRemoveSdRawTasks: bindActionCreators(removeSdRawTasks, dispatch),
  taskListSerialDilutionBusinessRemoveSdDiluentTargetRawTask: bindActionCreators(
    removeSdDiluentTargetRawTask,
    dispatch,
  ),
  taskListSerialDilutionBusinessIsSdStepDefined: bindActionCreators(isSdStepDefined, dispatch),
  taskListSerialDilutionBusinessHandleStepPropertiesChange: bindActionCreators(handleStepPropertiesChange, dispatch),
  taskListSerialDilutionBusinessHandleSDPropertyChange: bindActionCreators(handleSDPropertyChange, dispatch),
  taskListSerialDilutionBusinessApplyUniformedVolumeToNewSteps: bindActionCreators(
    applyUniformedVolumeToNewSteps,
    dispatch,
  ),
  undoRedoBusinessActionGlobalAddUndoItem: bindActionCreators(globalAddUndoItem, dispatch),
  serialDilutionActionBusinessSetSdPropertyObject: bindActionCreators(setSdPropertyObject, dispatch),
  serialDilutionActionBusinessRemoveSdPropertyObject: bindActionCreators(removeSdPropertyObject, dispatch),
});

// FIXME: only render when cardStateCards changed??
// eslint-disable-next-line arrow-body-style
const areEqual = (prevProps, nextProps) => prevProps.cardStateCards === nextProps.cardStateCards;

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(withIntl(SerialDilution)), areEqual);
